import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ManifestAsset } from '../shared/api.ts';
import styles from './Lightbox.module.css';

interface LightboxProps {
  assets: ManifestAsset[];
  openId: string;
  downloads: { original: boolean; fb: boolean; ig: boolean };
  onClose: () => void;
  onNavigate: (id: string) => void;
}

/** Label, variant key, and the suffix appended to the downloaded filename. */
const PHOTO_DOWNLOADS = [
  { key: 'original', label: 'Original', suffix: '' },
  { key: 'fb', label: 'Facebook · 2048px', suffix: '-2048' },
  { key: 'ig', label: 'Instagram · 1080px', suffix: '-1080' },
] as const;

const VIDEO_DOWNLOADS = [
  { key: 'original', label: 'Original quality', suffix: '' },
  { key: 'play', label: 'Web · 1080p', suffix: '-1080p' },
] as const;

export function Lightbox({
  assets,
  openId,
  downloads,
  onClose,
  onNavigate,
}: LightboxProps): React.JSX.Element | null {
  const index = assets.findIndex((a) => a.id === openId);
  const asset = assets[index];
  const [menuOpen, setMenuOpen] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  const go = useCallback(
    (delta: number) => {
      const next = assets[index + delta];
      if (next) {
        setMenuOpen(false);
        onNavigate(next.id);
      }
    },
    [assets, index, onNavigate],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        if (menuOpen) setMenuOpen(false);
        else onClose();
      }
      if (event.key === 'ArrowRight') go(1);
      if (event.key === 'ArrowLeft') go(-1);
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose, menuOpen]);

  // The page behind must not scroll while the overlay is up.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  // Preloaded, so arrowing through a gallery never shows a blank frame.
  useEffect(() => {
    for (const neighbour of [assets[index - 1], assets[index + 1]]) {
      const src = neighbour?.variants['fb'] ?? neighbour?.variants['t1800'];
      if (src) new Image().src = src;
    }
  }, [assets, index]);

  const options = useMemo(() => {
    if (!asset) return [];

    const source = asset.type === 'video' ? VIDEO_DOWNLOADS : PHOTO_DOWNLOADS;

    return source
      .filter((option) => {
        // The server already omits disallowed variants; this keeps the menu
        // honest if it ever sends one anyway.
        if (option.key === 'original') return downloads.original;
        if (option.key === 'fb') return downloads.fb;
        if (option.key === 'ig') return downloads.ig;
        return true;
      })
      .map((option) => ({ ...option, href: asset.variants[option.key] }))
      .filter((option): option is typeof option & { href: string } => Boolean(option.href));
  }, [asset, downloads]);

  if (!asset) return null;

  const display = asset.variants['fb'] ?? asset.variants['t1800'] ?? asset.variants['t900'];

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={asset.filename}
      onClick={(event) => {
        // Only the backdrop itself closes; the media and controls must not.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={styles.bar}>
        <span className={styles.counter}>
          {index + 1} / {assets.length}
        </span>

        <span className={styles.filename}>{asset.filename}</span>

        <div className={styles.barActions}>
          {options.length > 0 ? (
            <div className={styles.menuWrap}>
              <button
                type="button"
                className={styles.action}
                aria-expanded={menuOpen}
                aria-haspopup="menu"
                onClick={() => setMenuOpen((open) => !open)}
              >
                Download
              </button>

              {menuOpen ? (
                <div className={styles.menu} role="menu">
                  {options.map((option) => (
                    <a
                      key={option.key}
                      role="menuitem"
                      className={styles.menuItem}
                      href={option.href}
                      /*
                       * Same-origin, so `download` is honoured. The original
                       * also carries a Content-Disposition set at upload,
                       * which wins and restores the original filename.
                       */
                      download={downloadName(asset, option.suffix)}
                      onClick={() => setMenuOpen(false)}
                    >
                      {option.label}
                    </a>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <button type="button" className={styles.action} ref={closeRef} onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      <button
        type="button"
        className={`${styles.arrow} ${styles.prev}`}
        onClick={() => go(-1)}
        disabled={index === 0}
        aria-label="Previous"
      >
        &#8249;
      </button>

      <figure className={styles.stage}>
        {asset.type === 'video' && asset.variants['play'] ? (
          <video
            className={styles.media}
            src={asset.variants['play']}
            poster={asset.variants['poster']}
            controls
            playsInline
            preload="metadata"
          />
        ) : (
          <img className={styles.media} src={display} alt={asset.filename} />
        )}
      </figure>

      <button
        type="button"
        className={`${styles.arrow} ${styles.next}`}
        onClick={() => go(1)}
        disabled={index === assets.length - 1}
        aria-label="Next"
      >
        &#8250;
      </button>
    </div>
  );
}

function downloadName(asset: ManifestAsset, suffix: string): string {
  // The original keeps its own name and extension verbatim.
  if (suffix === '') return asset.filename;

  const dot = asset.filename.lastIndexOf('.');
  const base = dot > 0 ? asset.filename.slice(0, dot) : asset.filename;

  // The processor fixes every derivative's format -- JPEG for photos, MP4 for the
  // video proxy -- so reusing the source extension names a JPEG "...-2048.png".
  return `${base}${suffix}.${asset.type === 'video' ? 'mp4' : 'jpg'}`;
}
